import React from 'react';
import { View, TextInput, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { ThreadModerationList } from '@/components/admin/corkBoard/ThreadModerationList';
import { EditThreadTagsModal } from '@/components/admin/corkBoard/EditThreadTagsModal';
import { AnnouncementFormModal } from '@/components/admin/corkBoard/AnnouncementFormModal';
import { AdminSelectDropdown } from '@/components/common/admin/AdminSelectDropdown';
import { ADMIN_COLORS } from '@/constants/admin/colors';
import { CORK_BOARD_SORT_OPTIONS } from '@/constants/residents/corkBoardConstants';
import type { useThreadModeration } from '@/hooks/admin/useThreadModeration';
import type { CorkBoardThreadSummary, ThreadSortBy } from '@/types/residents/corkBoard';

interface DesktopThreadModerationViewProps {
  moderation: ReturnType<typeof useThreadModeration>;
  editTagsVisible: boolean;
  editingThread: CorkBoardThreadSummary | null;
  announcementVisible: boolean;
  onOpenEditTags: (thread: CorkBoardThreadSummary) => void;
  onCloseEditTags: () => void;
  onSaveTags: (threadId: string, tagIds: number[]) => void;
  onOpenAnnouncement: () => void;
  onCloseAnnouncement: () => void;
  onSaveAnnouncement: (data: { title: string; body: string; tagIds: number[] }) => void;
  onDelete: (thread: CorkBoardThreadSummary) => void;
}

export const DesktopThreadModerationView: React.FC<DesktopThreadModerationViewProps> = ({
  moderation,
  editTagsVisible,
  editingThread,
  announcementVisible,
  onOpenEditTags,
  onCloseEditTags,
  onSaveTags,
  onOpenAnnouncement,
  onCloseAnnouncement,
  onSaveAnnouncement,
  onDelete,
}) => {
  return (
    <DesktopAdminLayout>
      <DesktopPageHeader
        title="Thread Moderation"
        subtitle="Manage community threads: pin, lock, edit tags, delete, and create announcements."
        showAddButton
        onAddPress={onOpenAnnouncement}
        addButtonText="New Announcement"
      />

      <View style={styles.content}>
        {/* Search + sort row */}
        <View style={styles.searchRow}>
          <TextInput
            style={styles.searchInput}
            value={moderation.searchInput}
            onChangeText={moderation.setSearchInput}
            placeholder="Search threads..."
            placeholderTextColor={ADMIN_COLORS.TEXT_TERTIARY}
          />
          <AdminSelectDropdown
            label="Sort"
            options={CORK_BOARD_SORT_OPTIONS}
            selectedValue={moderation.sortBy}
            onSelect={(value) => moderation.setSortBy(value as ThreadSortBy)}
            variant="anchored"
          />
        </View>

        {moderation.error && (
          <View style={styles.errorBanner}>
            <Text style={styles.errorBannerText}>Failed to load threads.</Text>
            <TouchableOpacity onPress={moderation.refetch}>
              <Text style={styles.errorRetryText}>Retry</Text>
            </TouchableOpacity>
          </View>
        )}

        <View style={styles.card}>
          <View style={styles.cardHeader}>
            <Text style={styles.cardTitle}>Threads</Text>
            <Text style={styles.cardSubtitle}>
              {moderation.totalCount} thread{moderation.totalCount !== 1 ? 's' : ''}
            </Text>
          </View>

          <ThreadModerationList
            threads={moderation.threads}
            isLoading={moderation.isLoading}
            isMutating={moderation.isMutating}
            onPin={moderation.handlePinThread}
            onLock={moderation.handleLockThread}
            onEditTags={onOpenEditTags}
            onDelete={onDelete}
            onRefresh={moderation.refetch}
            isRefreshing={moderation.isFetching}
            onLoadMore={moderation.loadMore}
            hasNextPage={moderation.hasNextPage}
            isFetchingMore={moderation.isFetchingNextPage}
          />
        </View>
      </View>

      <EditThreadTagsModal
        visible={editTagsVisible}
        thread={editingThread}
        availableTags={moderation.tags}
        isSaving={moderation.isUpdatingTags}
        onSave={onSaveTags}
        onClose={onCloseEditTags}
      />
      <AnnouncementFormModal
        visible={announcementVisible}
        availableTags={moderation.tags}
        isSaving={moderation.isCreatingAnnouncement}
        onSave={onSaveAnnouncement}
        onClose={onCloseAnnouncement}
      />
    </DesktopAdminLayout>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
    gap: 16,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  searchInput: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.INPUT.PRIMARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.INPUT.BORDER,
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  card: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    overflow: 'hidden',
    flex: 1,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  cardSubtitle: {
    fontSize: 13,
    color: ADMIN_COLORS.TEXT.SECONDARY,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: '#FEF2F2',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#FECACA',
  },
  errorBannerText: {
    fontSize: 13,
    color: ADMIN_COLORS.ERROR,
  },
  errorRetryText: {
    fontSize: 13,
    fontWeight: '600',
    color: ADMIN_COLORS.ERROR,
    textDecorationLine: 'underline',
  },
});
